import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useMarkRead, useNotifications, useUnreadCount, type NotificationDto } from '../api/hooks'
import { fmtDateTime } from './ui'

const COLLABORATION_KINDS = ['CollaborationRequested', 'CollaborationDecided']

/** Where a notification leads: actions open in the actions list, everything else follows its link (shared-report notices → /reports/{id}). */
function targetOf(n: NotificationDto): string | null {
  if (n.link?.startsWith('/actions/')) return n.actionId ? `/actions?id=${n.actionId}` : '/actions'
  if (n.link?.startsWith('/')) return n.link
  if (n.formInstanceId) return `/reports/${n.formInstanceId}`
  return n.actionId ? `/actions?id=${n.actionId}` : null
}

export default function NotificationBell() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const unread = useUnreadCount()
  const list = useNotifications(false)
  const markRead = useMarkRead()
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onClick = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false)
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  return (
    <div className="bell" ref={ref}>
      <button className="ghost" onClick={() => setOpen((o) => !o)} aria-label={t('notifications.title')}>
        🔔{unread.data ? <span className="bell-count">{unread.data}</span> : null}
      </button>
      {open ? (
        <div className="bell-menu card">
          <div className="row between"><b>{t('notifications.title')}</b><button className="link" onClick={() => markRead.mutate({ all: true })}>{t('notifications.markAll')}</button></div>
          {!list.data?.items.length ? <p className="muted small">{t('notifications.empty')}</p> : list.data.items.map((n) => (
            <button key={n.id} className={`bell-item ${n.readAt ? '' : 'unread'}`} onClick={() => { const target = targetOf(n); markRead.mutate({ ids: [n.id] }); setOpen(false); if (target) navigate(target) }}>
              <div><b>{COLLABORATION_KINDS.includes(n.kind) ? <span aria-hidden="true">👥 </span> : null}{n.title || t(`notifications.kind.${n.kind}`, { defaultValue: t('notifications.title') })}</b></div>
              <div className="small">{n.body}</div>
              <div className="muted small">{fmtDateTime(n.createdAt)}</div>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
